import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiExternalLink } from 'react-icons/fi';
import { Certification } from '../types/resume.types';

interface CertificationsProps {
  certifications: Certification[];
}

const CertificationCard: React.FC<{ certification: Certification; index: number }> = ({ certification, index }) => {
  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white dark:bg-gray-800 p-6 rounded-2xl card-shadow hover:shadow-xl transition-all duration-300"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary-400 to-secondary-500">
          <FiAward className="text-white" size={24} />
        </div>
        
        {certification.credentialId && (
          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
            {certification.credentialId}
          </span>
        )}
      </div>

      <h3 className="font-bold text-gray-900 dark:text-white mb-2 leading-tight">
        {certification.name}
      </h3>
      
      <p className="font-semibold mb-3 text-primary-600 dark:text-primary-400">
        {certification.issuer}
      </p>

      {certification.date && (
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
          {certification.date}
        </p>
      )}

      {certification.url && (
        <motion.a
          href={certification.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          whileHover={{ x: 5 }}
        >
          <span className="text-sm font-medium">View Credential</span>
          <FiExternalLink size={14} />
        </motion.a>
      )}
    </motion.div>
  );
};

const Certifications: React.FC<CertificationsProps> = ({ certifications }) => {
  return (
    <section id="certifications" className="py-16 section-padding">
      <div className="container-width">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Certifications & Awards
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            Professional certifications and awards demonstrating expertise in testing and cloud technologies
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <CertificationCard key={cert.name} certification={cert} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Certifications;